/**
 * Native bridge — the web app's interface to native desktop capabilities.
 *
 * Each function checks if native tools are available and routes accordingly.
 * Returns null when native isn't available so the caller can fall back to
 * browser-side processing.
 */

import { isDesktop } from './fileSystem';
import * as cloud from './cloudbridge';
import { getIdToken } from './auth';
import { getProcessingMode } from './processingMode';

// Match the PROCESSING_URL used in cloudbridge so the HEAD probe goes to the
// right host.
const PROCESSING_URL = import.meta.env.VITE_PROCESSING_URL || 'https://process.scrivar.com';
const CLOUD_HEALTH_TIMEOUT_MS = 500;

let depsCache = null;

/**
 * Quick pre-flight check: can we reach the cloud right now?
 *
 *   1. We need an auth token (`getIdToken()` returns truthy)
 *   2. The /health endpoint responds within 500ms
 *
 * Used by compressPDF to decide whether to attempt a cloud call vs go
 * straight to local fallback. Saves uploading a large PDF only to fail.
 */
async function canReachCloud() {
  // Desktop requires a signed-in token to use the cloud (a not-signed-in desktop
  // user falls back to local engines). Web may use the cloud anonymously — the
  // processing service allows + rate-limits anonymous /compress — so a token is
  // optional there; we only probe reachability.
  if (isDesktopSafe()) {
    const token = await getIdToken().catch(() => null);
    if (!token) return false;
  }

  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), CLOUD_HEALTH_TIMEOUT_MS);
  try {
    const res = await fetch(`${PROCESSING_URL}/health`, { method: 'GET', signal: ac.signal });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Best-effort isDesktop check that doesn't blow up if `window` is undefined
 * (e.g. server-side rendering, certain test harnesses).
 */
function isDesktopSafe() {
  if (typeof window === 'undefined') return false;
  return !!window.electronAPI?.isDesktop;
}

/**
 * Get native dependency status. Cached after first call.
 * @returns {Promise<Object|null>} Deps object or null if not in desktop mode
 */
export async function getNativeDeps() {
  if (!isDesktop()) return null;
  if (depsCache) return depsCache;
  depsCache = await window.electronAPI.checkNativeDeps();
  return depsCache;
}

/**
 * Clear the deps cache (e.g. after user installs a tool).
 */
export function clearDepsCache() {
  depsCache = null;
}

/**
 * Check if a specific native tool is available.
 * @param {'ghostscript'|'libreoffice'|'tesseract'|'python'|'pymupdf'} tool
 * @returns {Promise<boolean>}
 */
export async function hasNative(tool) {
  const deps = await getNativeDeps();
  return deps?.[tool]?.found ?? false;
}

/**
 * Signal that a heavy op couldn't run because we're offline (or not signed in)
 * AND the offline engines aren't installed. A top-level host
 * (OfflineEnginesPromptHost in App.jsx) listens and offers the one-time
 * download. Guarded to desktop builds that expose the engine-pack IPC, so it's
 * a no-op on web. Fired only for ops the CORE pack enables (compress / OCR /
 * unlock / flatten) — NOT convert, whose engine ships in a separate add-on.
 */
function notifyOfflineEnginesNeeded() {
  if (typeof window !== 'undefined' && window.electronAPI?.enginePackStatus) {
    window.dispatchEvent(new CustomEvent('scrivar:offline-engines-needed'));
  }
}

// ─── Smart operation wrappers ───

/**
 * Desktop uses the local compression engines unless the user selects cloud
 * processing. Local engine failures never trigger an upload. Web uses the
 * cloud service, or returns null for the browser fallback when unreachable.
 *
 * @param {ArrayBuffer|Uint8Array} pdfBytes
 * @param {'low'|'medium'|'high'} level
 * @param {Object} [opts]
 * @param {(headers: Headers) => void} [opts.onMeta] — forwarded to cloudbridge
 *   so callers can read X-Scrivar-Compression-* headers.
 * @returns {Promise<ArrayBuffer|null>}
 */
export async function compressPDF(pdfBytes, level, opts = {}) {
  if (isDesktopSafe()) {
    if (getProcessingMode() === 'cloud') return cloud.compressPDF(pdfBytes, level, opts);
    if (!(await hasNative('qpdf'))) notifyOfflineEnginesNeeded();
    return window.electronAPI.nativeProcess('compress', { data: pdfBytes, level });
  }
  if (await canReachCloud()) return cloud.compressPDF(pdfBytes, level, opts);
  return null;
}

/**
 * Lossless PDF unlock using qpdf or return null for browser fallback.
 * @param {ArrayBuffer|Uint8Array} pdfBytes
 * @param {string} password
 * @returns {Promise<ArrayBuffer|null>} Unlocked bytes or null
 */
export async function unlockPDF(pdfBytes, password) {
  // Desktop uploads only when the user explicitly selects cloud processing.
  if (isDesktopSafe()) {
    if (getProcessingMode() === 'cloud') return cloud.unlockPDF(pdfBytes, password);
    if (await hasNative('qpdf')) {
      return window.electronAPI.nativeProcess('unlock', { data: pdfBytes, password });
    }
    notifyOfflineEnginesNeeded();
    return null;
  }
  // Web: cloud (unchanged).
  return cloud.unlockPDF(pdfBytes, password);
}

/**
 * AES-256 protect via qpdf (cloud or desktop native). There is NO browser
 * fallback — pdf-lib cannot encrypt — so web callers must handle thrown
 * errors and desktop callers must handle null (no engine available).
 */
export async function protectPDF(pdfBytes, opts) {
  if (isDesktopSafe()) {
    if (getProcessingMode() === 'cloud') return cloud.protectPDF(pdfBytes, opts);
    if (await hasNative('qpdf')) {
      return window.electronAPI.nativeProcess('protect', { data: pdfBytes, ...opts });
    }
    notifyOfflineEnginesNeeded();
    return null;
  }
  return cloud.protectPDF(pdfBytes, opts);
}

/**
 * Flatten PDF using qpdf or return null for browser fallback.
 * @param {ArrayBuffer|Uint8Array} pdfBytes
 * @returns {Promise<ArrayBuffer|null>} Flattened bytes or null
 */
export async function flattenPDF(pdfBytes) {
  // Desktop uploads only when the user explicitly selects cloud processing.
  if (isDesktopSafe()) {
    if (getProcessingMode() === 'cloud') return cloud.flattenPDF(pdfBytes);
    if (await hasNative('python')) {
      return window.electronAPI.nativeProcess('flatten', { data: pdfBytes });
    }
    notifyOfflineEnginesNeeded();
    return null;
  }
  // Web: cloud (unchanged).
  return cloud.flattenPDF(pdfBytes);
}

/**
 * Convert a file using LibreOffice or return null.
 * @param {ArrayBuffer|Uint8Array} fileBytes
 * @param {string} inputExt - e.g. '.docx', '.pdf'
 * @param {string} outputFormat - e.g. 'pdf', 'docx'
 * @returns {Promise<ArrayBuffer|null>} Converted bytes or null
 */
export async function convertFile(fileBytes, inputExt, outputFormat, options = {}) {
  // Local LibreOffice is the default; cloud conversion is an explicit choice.
  if (isDesktopSafe()) {
    if (getProcessingMode() === 'cloud') {
      if (options.pdfLayout === 'booklet') throw new Error('Booklet preparation uses local processing. Select On this device in Settings.');
      if (options.inputBytes > 50 * 1024 * 1024) throw new Error('This document exceeds the 50 MB cloud conversion limit. Select local processing in Settings to convert it on this device.');
      if (options.inputPath) fileBytes = await window.electronAPI.readFile(options.inputPath);
      return cloud.convertFile(fileBytes, inputExt, outputFormat);
    }
    if (await hasNative('libreoffice')) {
      if (options.inputPath) return window.electronAPI.convertLocalFile(options.inputPath, {pdfLayout: options.pdfLayout, bookletPages: options.bookletPages});
      return window.electronAPI.nativeProcess('convert', {
        data: fileBytes,
        ext: inputExt,
        outputFormat,
        pdfLayout: options.pdfLayout,
        bookletPages: options.bookletPages,
      });
    }
    return null;
  }
  // Web: cloud (unchanged).
  return cloud.convertFile(fileBytes, inputExt, outputFormat);
}

/**
 * Run native Tesseract OCR on an image or return null for browser fallback.
 * @param {ArrayBuffer|Uint8Array} imageBytes
 * @param {string} language - e.g. 'eng', 'eng+fra'
 * @returns {Promise<ArrayBuffer|null>} Searchable PDF bytes or null
 */
export async function ocrImage(imageBytes, language = 'eng', options = {}) {
  // The interactive OCR tool processes locally and requests a text-only PDF
  // so original graphics, links and unprocessed pages can be retained.
  if (options.localOnly) {
    if (isDesktopSafe() && await hasNative('tesseract')) {
      return window.electronAPI.nativeProcess('ocr', { data: imageBytes, ext: '.png', language,
        textOnly: true, dpi: options.dpi, psm: options.psm });
    }
    return null;
  }
  // Local OCR is the default; batch callers can explicitly select cloud.
  if (isDesktopSafe()) {
    if (getProcessingMode() === 'cloud') return cloud.ocrImage(imageBytes, language);
    if (await hasNative('tesseract')) {
      return window.electronAPI.nativeProcess('ocr', { data: imageBytes, ext: '.png', language });
    }
    notifyOfflineEnginesNeeded();
    return null;
  }
  // Web: cloud (unchanged).
  return cloud.ocrImage(imageBytes, language);
}

/**
 * Get installed Tesseract languages (native only).
 * @returns {Promise<string[]>} Language codes or empty array
 */
export async function getOCRLanguages() {
  if (!isDesktop() || !(await hasNative('tesseract'))) return [];
  return window.electronAPI.nativeProcess('ocr:languages', {});
}
